import { jsPDF } from "jspdf";

// Colors
const COLORS = {
    primary: [16, 185, 129],      // Emerald/Shopify green
    secondary: [5, 150, 105],     // Darker green
    dark: [10, 10, 10],
    text: [30, 30, 30],
    textLight: [100, 100, 100],
    success: [16, 185, 129],
    warning: [245, 158, 11],
    danger: [239, 68, 68],
    white: [255, 255, 255]
};

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_design-scorecard/artifacts/dmpk6oxa_site%20grade%20pro%20logo%20a.png";

// Feature details for PDF
const FEATURE_DETAILS = {
    abandoned_cart: {
        title: "Abandoned Cart Email Sequence",
        impact: "Recover 10-15% of lost sales",
        description: "Automated email reminders to customers who left items in cart",
        implementation: [
            "Enable Shopify's built-in abandoned checkout emails",
            "Or use Klaviyo for advanced sequences",
            "Set up 3-email series at 1hr, 24hr, 72hr intervals",
            "Include product images and easy checkout link"
        ],
        tools: ["Shopify Flow", "Klaviyo", "Omnisend"],
        difficulty: "Easy",
        timeToImplement: "2-4 hours"
    },
    sms_marketing: {
        title: "SMS Marketing Integration",
        impact: "98% open rate vs 20% email",
        description: "Text message marketing for promotions and updates",
        implementation: [
            "Choose SMS platform (Postscript, SMSBump)",
            "Set up opt-in popup on website",
            "Create welcome flow for new subscribers",
            "Build campaign calendar for promotions"
        ],
        tools: ["Postscript", "SMSBump", "Attentive"],
        difficulty: "Moderate",
        timeToImplement: "4-6 hours"
    },
    product_bundles: {
        title: "Product Bundles & Upsells",
        impact: "Increase AOV by 20-35%",
        description: "Offer curated product packages at discounted prices",
        implementation: [
            "Identify complementary products",
            "Create bundle offers with 10-15% discount",
            "Add bundle widgets to product pages",
            "Implement post-purchase upsells"
        ],
        tools: ["Bold Product Options", "Bundler", "ReConvert"],
        difficulty: "Moderate",
        timeToImplement: "3-5 hours"
    },
    product_reviews: {
        title: "Customer Review System",
        impact: "Products with reviews convert 270% better",
        description: "Collect and display customer feedback",
        implementation: [
            "Install review app (Judge.me, Loox)",
            "Set up automated review request emails",
            "Display star ratings on collection pages",
            "Add photo review incentives"
        ],
        tools: ["Judge.me", "Loox", "Yotpo"],
        difficulty: "Easy",
        timeToImplement: "1-2 hours"
    },
    optimized_descriptions: {
        title: "SEO-Optimized Product Descriptions",
        impact: "Improve organic traffic by 50%+",
        description: "Keyword-rich descriptions that convert visitors",
        implementation: [
            "Research target keywords for each product",
            "Write benefit-focused descriptions",
            "Include technical specs in structured format",
            "Add meta titles and descriptions"
        ],
        tools: ["SEMrush", "Ahrefs", "Google Keyword Planner"],
        difficulty: "Moderate",
        timeToImplement: "2-4 hours per product"
    },
    free_shipping: {
        title: "Free Shipping Threshold",
        impact: "Increase AOV by 15-25%",
        description: "Incentivize larger orders with free shipping",
        implementation: [
            "Calculate profitable shipping threshold",
            "Set up conditional free shipping in Shopify",
            "Add progress bar to cart page",
            "Promote threshold across site"
        ],
        tools: ["Shopify Settings", "Free Shipping Bar app"],
        difficulty: "Easy",
        timeToImplement: "1-2 hours"
    },
    conversion_tracking: {
        title: "Advanced Conversion Tracking",
        impact: "Optimize ad spend with accurate data",
        description: "Track customer journey from ad to purchase",
        implementation: [
            "Install Facebook Pixel and Conversions API",
            "Set up Google Analytics 4 ecommerce",
            "Configure conversion events",
            "Enable server-side tracking"
        ],
        tools: ["Facebook Pixel", "Google Analytics 4", "Elevar"],
        difficulty: "Advanced",
        timeToImplement: "4-8 hours"
    }
};

// Helper to add page number
const addPageNumber = (doc, pageNum) => {
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.textLight);
    doc.text(`${pageNum}`, doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
};

// Helper to add footer
const addFooter = (doc) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.textLight);
    doc.text("Generated by SiteGradePro.com", doc.internal.pageSize.getWidth() / 2, pageHeight - 5, { align: 'center' });
};

// Main PDF Generator
export async function generateShopifyFixPlanPDF(data) {
    const {
        businessName = "Your Store",
        websiteUrl = "yourstore.myshopify.com",
        answers = {},
        score = 0,
        maxScore = 70,
        grade = { letter: "C", label: "Average" },
        leadData = null
    } = data;

    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let pageNum = 1;

    // Try to load logo image
    let logoImg = null;
    try {
        const response = await fetch(LOGO_URL);
        const blob = await response.blob();
        logoImg = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
        });
    } catch (e) {
        console.log("Could not load logo for PDF");
    }

    // Get missing features (answered "No")
    const missingFeatures = Object.entries(answers)
        .filter(([key, value]) => value === false && FEATURE_DETAILS[key])
        .map(([key]) => ({ id: key, ...FEATURE_DETAILS[key] }));

    const implementedFeatures = Object.entries(answers)
        .filter(([key, value]) => value === true && FEATURE_DETAILS[key])
        .map(([key]) => ({ id: key, ...FEATURE_DETAILS[key] }));

    // ========== PAGE 1: COVER PAGE ==========
    doc.setFillColor(...COLORS.dark);
    doc.rect(0, 0, pageWidth, 100, 'F');
    
    // Green accent line (Shopify brand)
    doc.setFillColor(...COLORS.primary);
    doc.rect(0, 98, pageWidth, 3, 'F');

    // Logo text
    doc.setFontSize(14);
    doc.setTextColor(...COLORS.primary);
    doc.setFont('helvetica', 'bold');
    doc.text("SITE GRADE PRO", margin, 30);

    // Main title
    doc.setFontSize(32);
    doc.setTextColor(...COLORS.white);
    doc.text("Shopify", margin, 55);
    doc.setTextColor(...COLORS.primary);
    doc.text("Fix Plan", margin, 70);
    doc.setFontSize(12);
    doc.setTextColor(...COLORS.white);
    doc.text("TM", margin + 43, 62);

    // Report details box
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(margin, 115, contentWidth, 50, 3, 3, 'F');
    
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.textLight);
    doc.text("STORE OPTIMIZATION REPORT FOR:", margin + 10, 128);
    
    doc.setFontSize(18);
    doc.setTextColor(...COLORS.text);
    doc.setFont('helvetica', 'bold');
    doc.text(leadData?.name || businessName, margin + 10, 142);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.primary);
    doc.text(leadData?.website || websiteUrl, margin + 10, 152);
    
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.textLight);
    doc.text(new Date().toLocaleDateString('en-US', { 
        year: 'numeric', month: 'long', day: 'numeric' 
    }), margin + 10, 160);

    // Grade preview
    const gradeX = pageWidth - margin - 50;
    const gradeColor = score >= 50 ? COLORS.success : score >= 30 ? COLORS.warning : COLORS.danger;
    
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(gradeX - 5, 115, 60, 50, 3, 3, 'F');
    doc.setDrawColor(...gradeColor);
    doc.setLineWidth(2);
    doc.roundedRect(gradeX - 5, 115, 60, 50, 3, 3, 'S');
    
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.textLight);
    doc.text("STORE GRADE", gradeX + 25, 128, { align: 'center' });
    
    doc.setFontSize(36);
    doc.setTextColor(...gradeColor);
    doc.setFont('helvetica', 'bold');
    doc.text(grade.letter, gradeX + 25, 150, { align: 'center' });
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.textLight);
    doc.text(grade.label, gradeX + 25, 160, { align: 'center' });

    addFooter(doc);
    addPageNumber(doc, pageNum);

    // ========== PAGE 2: EXECUTIVE SUMMARY ==========
    doc.addPage();
    pageNum++;

    doc.setFillColor(...COLORS.dark);
    doc.rect(0, 0, pageWidth, 25, 'F');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.text("STORE ASSESSMENT", margin, 16);
    // Add logo to header
    if (logoImg) {
        try { doc.addImage(logoImg, 'PNG', pageWidth - margin - 30, 5, 28, 14); } catch (e) {}
    }

    let yPos = 40;

    // Score breakdown
    doc.setFontSize(14);
    doc.setTextColor(...COLORS.text);
    doc.setFont('helvetica', 'bold');
    doc.text("Optimization Score", margin, yPos);
    yPos += 15;

    // Score bar
    doc.setFillColor(230, 230, 230);
    doc.roundedRect(margin, yPos, contentWidth, 12, 3, 3, 'F');
    doc.setFillColor(...gradeColor);
    doc.roundedRect(margin, yPos, contentWidth * (score / maxScore), 12, 3, 3, 'F');
    
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.text(`${score} / ${maxScore}`, margin + 10, yPos + 8.5);

    yPos += 25;

    // Summary stats
    const stats = [
        { label: "Features Implemented", value: implementedFeatures.length, color: COLORS.success },
        { label: "Features Missing", value: missingFeatures.length, color: COLORS.danger },
        { label: "Optimization Level", value: `${Math.round((score / maxScore) * 100)}%`, color: gradeColor }
    ];

    const statWidth = (contentWidth - 20) / 3;
    stats.forEach((stat, i) => {
        const x = margin + (i * (statWidth + 10));
        
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(x, yPos, statWidth, 35, 3, 3, 'F');
        
        doc.setFontSize(22);
        doc.setTextColor(...stat.color);
        doc.setFont('helvetica', 'bold');
        doc.text(String(stat.value), x + statWidth / 2, yPos + 18, { align: 'center' });
        
        doc.setFontSize(8);
        doc.setTextColor(...COLORS.textLight);
        doc.text(stat.label, x + statWidth / 2, yPos + 28, { align: 'center' });
    });

    yPos += 50;

    // What this means
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(margin, yPos, contentWidth, 40, 3, 3, 'F');
    
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.text);
    doc.setFont('helvetica', 'bold');
    doc.text("What This Means", margin + 10, yPos + 12);
    
    let summaryText;
    if (missingFeatures.length === 0) {
        summaryText = "Excellent! Your store has all key optimization features implemented. Focus on refining and testing for maximum conversions.";
    } else if (missingFeatures.length <= 2) {
        summaryText = `Your store is well-optimized with just ${missingFeatures.length} feature${missingFeatures.length > 1 ? 's' : ''} to add. Implementing these could increase revenue by 15-25%.`;
    } else if (missingFeatures.length <= 4) {
        summaryText = `Your store is missing ${missingFeatures.length} revenue-driving features. Addressing these gaps could significantly improve conversions and average order value.`;
    } else {
        summaryText = `Critical: Your store is missing ${missingFeatures.length} essential features. This is likely costing you 30-50% in potential revenue. Immediate optimization recommended.`;
    }
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const summaryLines = doc.splitTextToSize(summaryText, contentWidth - 20);
    doc.text(summaryLines, margin + 10, yPos + 22);

    addFooter(doc);
    addPageNumber(doc, pageNum);

    // ========== PAGES 3-N: MISSING FEATURES ==========
    if (missingFeatures.length > 0) {
        doc.addPage();
        pageNum++;

        doc.setFillColor(...COLORS.dark);
        doc.rect(0, 0, pageWidth, 25, 'F');
        doc.setFontSize(10);
        doc.setTextColor(...COLORS.white);
        doc.setFont('helvetica', 'bold');
        doc.text("MISSING FEATURES", margin, 16);
        // Add logo to header
        if (logoImg) {
            try { doc.addImage(logoImg, 'PNG', pageWidth - margin - 30, 5, 28, 14); } catch (e) {}
        }

        yPos = 35;

        missingFeatures.forEach((feature, index) => {
            if (yPos > pageHeight - 80) {
                addFooter(doc);
                addPageNumber(doc, pageNum);
                doc.addPage();
                pageNum++;
                yPos = 25;
            }

            // Feature card
            doc.setFillColor(248, 250, 252);
            doc.roundedRect(margin, yPos, contentWidth, 55, 3, 3, 'F');
            doc.setDrawColor(...COLORS.danger);
            doc.setLineWidth(0.3);
            doc.roundedRect(margin, yPos, contentWidth, 55, 3, 3, 'S');

            // Feature number
            doc.setFillColor(...COLORS.danger);
            doc.roundedRect(margin + 5, yPos + 5, 8, 8, 2, 2, 'F');
            doc.setFontSize(8);
            doc.setTextColor(...COLORS.white);
            doc.setFont('helvetica', 'bold');
            doc.text(`${index + 1}`, margin + 9, yPos + 10.5, { align: 'center' });

            // Title
            doc.setFontSize(11);
            doc.setTextColor(...COLORS.text);
            doc.text(feature.title, margin + 18, yPos + 11);

            // Impact badge
            doc.setFillColor(...COLORS.primary);
            doc.roundedRect(pageWidth - margin - 45, yPos + 5, 40, 7, 2, 2, 'F');
            doc.setFontSize(6);
            doc.setTextColor(...COLORS.white);
            doc.text(feature.impact.substring(0, 25), pageWidth - margin - 25, yPos + 9.5, { align: 'center' });

            // Description
            doc.setFontSize(8);
            doc.setTextColor(...COLORS.textLight);
            doc.setFont('helvetica', 'normal');
            doc.text(feature.description, margin + 8, yPos + 22);

            // Quick implementation
            doc.setFontSize(7);
            doc.setTextColor(...COLORS.text);
            doc.setFont('helvetica', 'bold');
            doc.text("Quick start:", margin + 8, yPos + 32);
            doc.setFont('helvetica', 'normal');
            doc.text(feature.implementation[0], margin + 28, yPos + 32);

            // Tools & Time
            doc.setFontSize(7);
            doc.setTextColor(...COLORS.textLight);
            doc.text(`Tools: ${feature.tools.slice(0, 2).join(", ")}`, margin + 8, yPos + 42);
            doc.text(`Time: ${feature.timeToImplement}`, margin + 8, yPos + 48);

            yPos += 62;
        });

        addFooter(doc);
        addPageNumber(doc, pageNum);
    }

    // ========== IMPLEMENTATION PRIORITY PAGE ==========
    doc.addPage();
    pageNum++;

    doc.setFillColor(...COLORS.dark);
    doc.rect(0, 0, pageWidth, 25, 'F');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.text("IMPLEMENTATION PRIORITY", margin, 16);
    // Add logo to header
    if (logoImg) {
        try { doc.addImage(logoImg, 'PNG', pageWidth - margin - 30, 5, 28, 14); } catch (e) {}
    }

    yPos = 40;

    doc.setFontSize(12);
    doc.setTextColor(...COLORS.text);
    doc.setFont('helvetica', 'bold');
    doc.text("Recommended Fix Order", margin, yPos);
    yPos += 15;

    // Priority list (sorted by difficulty)
    const sortedFeatures = [...missingFeatures].sort((a, b) => {
        const order = { "Easy": 0, "Moderate": 1, "Advanced": 2 };
        return order[a.difficulty] - order[b.difficulty];
    });

    sortedFeatures.forEach((feature, i) => {
        const diffColors = {
            "Easy": COLORS.success,
            "Moderate": COLORS.warning,
            "Advanced": COLORS.danger
        };
        
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(margin, yPos, contentWidth, 12, 2, 2, 'F');
        
        // Priority number
        doc.setFillColor(...COLORS.primary);
        doc.circle(margin + 8, yPos + 6, 4, 'F');
        doc.setFontSize(8);
        doc.setTextColor(...COLORS.white);
        doc.setFont('helvetica', 'bold');
        doc.text(`${i + 1}`, margin + 8, yPos + 8, { align: 'center' });
        
        // Title
        doc.setFontSize(9);
        doc.setTextColor(...COLORS.text);
        doc.text(feature.title, margin + 18, yPos + 8);
        
        // Difficulty badge
        doc.setFillColor(...diffColors[feature.difficulty]);
        doc.roundedRect(pageWidth - margin - 25, yPos + 2, 20, 7, 2, 2, 'F');
        doc.setFontSize(6);
        doc.setTextColor(...COLORS.white);
        doc.text(feature.difficulty.toUpperCase(), pageWidth - margin - 15, yPos + 6.5, { align: 'center' });
        
        yPos += 16;
    });

    addFooter(doc);
    addPageNumber(doc, pageNum);

    // ========== CLOSING PAGE ==========
    doc.addPage();
    pageNum++;

    doc.setFillColor(...COLORS.dark);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');

    // Green accent bar
    doc.setFillColor(...COLORS.primary);
    doc.rect(0, pageHeight / 2 - 50, pageWidth, 3, 'F');

    doc.setFontSize(22);
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.text("Your Shopify Store", pageWidth / 2, pageHeight / 2 - 25, { align: 'center' });
    doc.setTextColor(...COLORS.primary);
    doc.text("Deserves To Convert", pageWidth / 2, pageHeight / 2 - 5, { align: 'center' });

    doc.setFontSize(10);
    doc.setTextColor(...COLORS.textLight);
    doc.setFont('helvetica', 'normal');
    const closingText = "This optimization plan identifies the key features missing from your store. Implementing these changes can significantly improve your conversion rate and average order value.";
    const closingLines = doc.splitTextToSize(closingText, contentWidth - 20);
    doc.text(closingLines, pageWidth / 2, pageHeight / 2 + 25, { align: 'center' });

    // CTA with clickable email link
    const buttonX = pageWidth / 2 - 45;
    const buttonY = pageHeight / 2 + 55;
    const buttonW = 90;
    const buttonH = 14;
    
    doc.setFillColor(...COLORS.primary);
    doc.roundedRect(buttonX, buttonY, buttonW, buttonH, 3, 3, 'F');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.text("Schedule Consultation", pageWidth / 2, pageHeight / 2 + 64, { align: 'center' });
    
    // Add clickable email link
    doc.link(buttonX, buttonY, buttonW, buttonH, { url: 'mailto:hello@sitegradepro.com?subject=Shopify%20Optimization%20-%20Consultation%20Request' });

    doc.setFontSize(8);
    doc.setTextColor(...COLORS.textLight);
    doc.text("Email: hello@sitegradepro.com", pageWidth / 2, pageHeight / 2 + 80, { align: 'center' });

    // Logo
    doc.setFontSize(12);
    doc.setTextColor(...COLORS.primary);
    doc.setFont('helvetica', 'bold');
    doc.text("SITE GRADE PRO", pageWidth / 2, pageHeight - 40, { align: 'center' });
    
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.textLight);
    doc.setFont('helvetica', 'normal');
    doc.text("SiteGradePro.com", pageWidth / 2, pageHeight - 30, { align: 'center' });

    addPageNumber(doc, pageNum);

    return doc;
}

// Export function to download the PDF
export async function downloadShopifyFixPlanPDF(data) {
    const doc = await generateShopifyFixPlanPDF(data);
    const fileName = `shopify-fix-plan-${data.leadData?.website?.replace(/[^a-z0-9]/gi, '-') || 'report'}.pdf`;
    doc.save(fileName);
    return fileName;
}
